import React from 'react';
import { cn } from '../../utils/cn';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarTone = 'auto' | 'neutral' | 'brand' | 'emerald' | 'amber' | 'rose';

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Full name used to derive initials if `initials` is not provided. */
  name?: string;
  /** Explicit initials. Takes precedence over `name` derivation. */
  initials?: string;
  /** Optional avatar image URL. When present and loads, replaces initials. */
  src?: string;
  alt?: string;
  size?: AvatarSize;
  /** 'auto' derives a stable tone from the name hash, or falls back to neutral. */
  tone?: AvatarTone;
  /** Render a ring around the avatar (e.g., for current-user indicator). */
  ring?: boolean;
}

const sizeMap: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-11 w-11 text-base',
  xl: 'h-14 w-14 text-lg',
};

const toneMap: Record<Exclude<AvatarTone, 'auto'>, string> = {
  neutral:
    'bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200',
  brand:
    'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
  emerald:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  amber:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  rose:
    'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200',
};

const autoPalette: Array<Exclude<AvatarTone, 'auto'>> = [
  'brand',
  'emerald',
  'amber',
  'rose',
  'neutral',
];

function hashString(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function deriveInitials(name?: string, initials?: string): string {
  if (initials && initials.trim()) return initials.trim().slice(0, 2).toUpperCase();
  if (!name) return '?';
  const clean = name.trim().replace(/\s+/g, ' ');
  if (!clean) return '?';
  const parts = clean.split(' ').filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function resolveTone(tone: AvatarTone, seed: string): Exclude<AvatarTone, 'auto'> {
  if (tone !== 'auto') return tone;
  if (!seed) return 'neutral';
  return autoPalette[hashString(seed) % autoPalette.length];
}

/**
 * Avatar chip. Shows `src` if provided, falls back to initials derived from `name`.
 * Use `tone="auto"` to get a stable, name-hashed color so the same person
 * always shows the same chip across the app.
 */
export function Avatar({
  name,
  initials,
  src,
  alt,
  size = 'md',
  tone = 'auto',
  ring = false,
  className,
  ...rest
}: AvatarProps) {
  const [failed, setFailed] = React.useState(false);
  const resolvedInitials = deriveInitials(name, initials);
  const resolvedTone = resolveTone(tone, name || initials || '');
  const showImage = !!src && !failed;

  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold select-none',
        sizeMap[size],
        !showImage && toneMap[resolvedTone],
        ring &&
          'ring-2 ring-offset-2 ring-brand-500 ring-offset-white dark:ring-brand-400 dark:ring-offset-neutral-900',
        className
      )}
      aria-label={alt || name || 'Avatar'}
      {...rest}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt || name || ''}
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden="true">{resolvedInitials}</span>
      )}
    </span>
  );
}

export default Avatar;
